import { globalDefOrgLang, globalOrgUnitId, globalUserLang } from 'App/AppContextProvider';
import { AxiosHeaders, AxiosRequestConfig, AxiosResponse, create } from 'axios';
import { isAbortOrCancelError } from 'components/Notification/ErrorMessageHelper';
import * as rax from 'retry-axios';
import AppError from 'utils/appError';
import { nullsToUndefined } from 'utils/object';
import Config from '../Config/configService';
import logger from '../Logging/logService';

// ---- augment AxiosRequestConfig to safely store per-request debug stack ----
declare module 'axios' {
  export interface AxiosRequestConfig {
    __thrownAtStack?: string;
    __rateLimitLoggedAttempt?: number; // prevent duplicate logging for same retry attempt
  }
}

//create global instance of Axios
const instance = create({
  baseURL: Config.get('Api.BaseURL'),
});

// ------------------------------
// Rate-limit debug helpers
// ------------------------------
const RL_HEADERS = {
  retryAfter: 'retry-after',
  hit: 'x-ratelimit-hit',
  partition: 'x-ratelimit-partition',
  caller: 'x-ratelimit-caller',
  clientIp: 'x-ratelimit-clientip',
} as const;

function getHeader(resp: AxiosResponse | undefined, name: string): string | undefined {
  if (!resp?.headers) return undefined;
  // Axios normalizes header keys to lower-case in the browser.
  const v = resp.headers[name.toLowerCase()];
  if (v == null) return undefined;

  return Array.isArray(v) ? v.join(',') : String(v);
}

function getFileNameFromContentDispositionHeader(contentDisposition: string | undefined): string {
  if (!contentDisposition) {
    return '';
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    return decodeURIComponent(utf8Match[1]).trim();
  }

  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  if (asciiMatch && asciiMatch[1]) {
    return asciiMatch[1].trim();
  }

  return '';
}

export function getFileNameFromResponse(response: AxiosResponse | undefined): string {
  return getFileNameFromContentDispositionHeader(getHeader(response, 'content-disposition'));
}

function logRateLimitDetails(
  phase: 'retrying' | 'final_fail',
  error: { response?: AxiosResponse; config?: AxiosRequestConfig },
  attempt: number,
  retries: number,
) {
  const resp: AxiosResponse | undefined = error?.response;
  const status = resp?.status;
  if (status !== 429) return;

  // Avoid duplicate logs for same attempt (since multiple interceptors can fire)
  const cfg: AxiosRequestConfig | undefined = error?.config;
  if (cfg) {
    if (cfg.__rateLimitLoggedAttempt === attempt) return;
    cfg.__rateLimitLoggedAttempt = attempt;
  }

  const url = error?.config?.url;
  const method = error?.config?.method?.toUpperCase();

  const hdr = {
    retryAfter: getHeader(resp, RL_HEADERS.retryAfter),
    hit: getHeader(resp, RL_HEADERS.hit),
    partition: getHeader(resp, RL_HEADERS.partition),
    caller: getHeader(resp, RL_HEADERS.caller),
    clientIp: getHeader(resp, RL_HEADERS.clientIp),
  };

  // Log as structured payload so your logger can ship it cleanly
  logger.debug(`Rate limited (${phase}) ${attempt}/${retries} ${method ?? 'METHOD'} ${url ?? 'URL'}`, {
    http: { status, method, url },
    headers: hdr,
  });
}

// ------------------------------
// Retry configuration (retry-axios)
// ------------------------------
// IMPORTANT (browser): Ensure your API exposes these headers via CORS:
//   Access-Control-Expose-Headers: Retry-After, X-RateLimit-Hit, X-RateLimit-Partition, X-RateLimit-Caller, X-RateLimit-ClientIp
//
const MAX_WAIT_MS = 5000;

instance.defaults.raxConfig = {
  retry: 3,
  statusCodesToRetry: [
    [429, 429],
    [500, 599],
  ],
  httpMethodsToRetry: ['GET', 'HEAD', 'OPTIONS', 'DELETE', 'PUT'],
  backoffType: 'exponential',
  retryDelay: 250,
  checkRetryAfter: true,
  maxRetryDelay: MAX_WAIT_MS,
  maxRetryAfter: MAX_WAIT_MS,
  jitter: 'full',
  onRetryAttempt: async (err) => {
    const cfg = rax.getConfig(err);
    const attempt = cfg?.currentRetryAttempt ?? 0;
    const retries = cfg?.retry ?? 0;

    const status = err?.response?.status;
    const url = err?.config?.url;

    // Existing log
    logger.debug(`HTTP retrying (${attempt}) ${status ?? 'NETWORK'} ${url}`);

    // if 429, log RL details at the moment of retry
    logRateLimitDetails('retrying', err, attempt, retries);
  },
};

rax.attach(instance);

// log each request
instance.interceptors.request.use((request) => {
  request.__thrownAtStack = new Error('Thrown at:').stack;
  logger.debug(`${request.method?.toUpperCase() ?? 'METHOD'} ${request.url ?? 'URL'}`);

  return request;
});

// log each error
instance.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const cfg = rax.getConfig(error);
    const retriesAttempted = cfg?.currentRetryAttempt ?? 0; // 0 means “no retries happened”
    const totalAttempts = 1 + retriesAttempted;
    const retries = cfg?.retry ?? 0;

    const status = error?.response?.status;
    const url = error?.config?.url;

    // New: if we ended up failing with a 429, log RL details once more (final)
    logRateLimitDetails('final_fail', error, retriesAttempted, retries);

    // A 499 (server "client closed request") or a client-side cancellation
    // (navigate-away, component unmount) is not a real failure. Reuse the shared
    // abort/cancel detection so this stays consistent with the toast/ticket layers,
    // and log it at debug instead of error to avoid noise. Build a minimal AppError
    // (code + message only) to skip the expensive full normalizeError stringify.
    const isClientCancelled =
      status === 499 || isAbortOrCancelError(new AppError(error?.message ?? '', error?.code));
    const message = `HTTP failed after ${totalAttempts} attempt(s), ${retriesAttempted}/${retries} retries (${status}) ${url}`;

    if (isClientCancelled) {
      logger.debug(message);
    } else {
      logger.error(new AppError(message));
    }

    return Promise.reject(error);
  },
);

//replace all null values with undefined
instance.interceptors.response.use((respons) => {
  try {
    respons.data = nullsToUndefined(respons.data);
  } catch {
    //ignore
  }

  return respons;
});

//get a request configuration object for the back-end API
function getRequestConfig(
  accessToken: string,
  orgUnitId?: string,
  customerId?: string,
  abortSignal?: AbortSignal,
): AxiosRequestConfig {
  const config: AxiosRequestConfig = {};
  const headers = new AxiosHeaders();

  //set access token
  headers.set('authorization', 'Bearer ' + accessToken);

  //org unit can be supplied to individual requests, overwriting the global setting
  if (globalOrgUnitId || orgUnitId) {
    headers.set('orgunitid', orgUnitId ?? globalOrgUnitId);
  }

  //set global UI language
  if (globalUserLang) {
    headers.set('userlang', globalUserLang);
  }

  //set default organizational language
  if (globalDefOrgLang) {
    headers.set('deforglang', globalDefOrgLang);
  }

  //set requested customer
  if (customerId) {
    headers.set('customerid', customerId);
  }

  config.headers = headers.toJSON();

  if (abortSignal) {
    config.signal = abortSignal;
  }

  return config;
}

const httpService = {
  get: instance.get,
  put: instance.put,
  post: instance.post,
  delete: instance.delete,
  getRequestConfig,
};

export default httpService;
